import React, { useEffect, useState } from 'react';
import { Check, Book, Moon, Heart, Compass, AlertTriangle, Clock, ShieldAlert, EyeOff, FileText, FlaskConical, Leaf } from 'lucide-react';
import { Section, CTAButton } from './Shared';
import { FeatherQuill, MoonPhase } from './HealingIcons';
import { Pricing } from './Pricing';
const LaunchUrgency = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 18,
    s: 5
  });
  const [spotsTaken, setSpotsTaken] = useState(() => {
    const saved = typeof window !== 'undefined' ? sessionStorage.getItem('akashico-launch-spots') : null;
    if (saved) {
      const {
        value,
        timestamp
      } = JSON.parse(saved);
      const elapsed = (Date.now() - timestamp) / 1000;
      const increment = Math.floor(elapsed / 60);
      return Math.min(value + increment, 92);
    }
    return 62;
  });
  useEffect(() => {
    sessionStorage.setItem('akashico-launch-spots', JSON.stringify({
      value: spotsTaken,
      timestamp: Date.now()
    }));
  }, [spotsTaken]);
  useEffect(() => {
    const id = setInterval(() => {
      setTimeLeft(prev => {
        let {
          h,
          m,
          s
        } = prev;
        if (s > 0) s--;else {
          s = 59;
          if (m > 0) m--;else {
            m = 59;
            if (h > 0) h--;
          }
        }
        return {
          h,
          m,
          s
        };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => {
      setSpotsTaken(prev => {
        if (prev >= 92) return prev;
        return Math.random() < 0.4 ? prev + 1 : prev;
      });
    }, 45000 + Math.random() * 30000);
    return () => clearInterval(id);
  }, []);
  const fmt = (n: number) => n.toString().padStart(2, '0');
  const TimeBox = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="flex flex-col items-center" data-id="element-243">
      <div className="bg-brand-purple text-white font-mono font-bold text-2xl md:text-4xl rounded-xl px-3 md:px-5 py-2 md:py-3 min-w-[56px] md:min-w-[80px] text-center shadow-md tabular-nums border border-brand-purpleLight/40" data-id="element-244">
        {fmt(value)}
      </div>
      <span className="text-[10px] md:text-sm font-medium text-brand-text/70 mt-1.5 uppercase tracking-wider" data-id="element-245">
        {label}
      </span>
    </div>;
  return <div className="max-w-4xl mx-auto mb-12" data-id="element-246">
      <div className="glass-panel rounded-3xl p-6 md:p-8 border-2 border-brand-purple/40 bg-gradient-to-br from-white/95 to-brand-purpleLight/10 shadow-[0_10px_40px_rgba(124,58,237,0.12)]" data-id="element-247">
        <div className="flex items-center justify-center gap-2 mb-5" data-id="element-248">
          <Clock className="w-5 h-5 md:w-6 md:h-6 text-brand-purple shrink-0" data-id="element-249" />
          <h3 className="font-serif font-bold text-base md:text-xl text-brand-purpleDark text-center" data-id="element-250">
            ⏰ PRECIO DE LANZAMIENTO — CIERRA EN:
          </h3>
        </div>

        <div className="flex items-center justify-center gap-2 md:gap-4 mb-5" data-id="element-251">
          <TimeBox value={timeLeft.h} label="Horas" data-id="element-252" />
          <span className="text-2xl md:text-4xl font-bold text-brand-purple/60 pb-5" data-id="element-253">
            :
          </span>
          <TimeBox value={timeLeft.m} label="Min" data-id="element-254" />
          <span className="text-2xl md:text-4xl font-bold text-brand-purple/60 pb-5" data-id="element-255">
            :
          </span>
          <TimeBox value={timeLeft.s} label="Seg" data-id="element-256" />
        </div>

        <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-300/60 rounded-xl px-4 py-3 mb-6" data-id="element-257">
          <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-amber-600 shrink-0" data-id="element-258" />
          <p className="text-sm md:text-base font-semibold text-amber-900 text-center" data-id="element-259">
            Después aumenta.{' '}
            <span className="text-brand-purple" data-id="element-260">
              No sabemos cuándo volverá esta oferta.
            </span>
          </p>
        </div>

        <div className="bg-white/80 rounded-2xl p-4 md:p-5 border border-brand-purple/30" data-id="element-261">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3" data-id="element-262">
            <div className="flex items-center gap-2" data-id="element-263">
              <span className="relative flex h-2.5 w-2.5" data-id="element-264">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-burgundy opacity-75" data-id="element-265"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-burgundy" data-id="element-266"></span>
              </span>
              <span className="font-semibold text-sm md:text-base text-brand-text" data-id="element-267">
                Cupos al precio de lanzamiento
              </span>
            </div>
            <span className="text-xs md:text-sm font-bold text-brand-purple uppercase tracking-wide" data-id="element-268">
              Quedan pocos
            </span>
          </div>

          <div className="w-full bg-brand-purple/15 rounded-full h-2.5 overflow-hidden mb-2" data-id="element-269">
            <div className="bg-gradient-to-r from-brand-purple to-brand-purpleDark h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
            width: `${spotsTaken}%`
          }} data-id="element-270" />
          </div>

          <p className="text-xs md:text-sm text-brand-text/70 text-center" data-id="element-271">
            🔥 <strong className="text-brand-purple" data-id="element-272">{spotsTaken}%</strong> de
            los cupos promocionales ya fueron tomados
          </p>
        </div>
      </div>
    </div>;
};
const tratadoFeatures = ['✅ Cómo estructurar una sesión profesional de Registros Akáshicos.', '✅ Herramientas para interpretar mensajes y símbolos con mayor claridad.', '✅ Protocolos de intervención para desbloquear procesos estancados.', '✅ Preguntas poderosas para profundizar cada lectura.', '✅ Protección energética antes, durante y después de la sesión.', '✅ Recursos para acompañar transformaciones profundas.', '✅ Estrategias para sostener procesos terapéuticos en el tiempo.', '✅ Casos prácticos y ejemplos aplicados.'];
const bonuses = [{
  icon: FileText,
  title: 'HUELLA DEL NOMBRE',
  pages: 'Bonus Estratégico',
  color: 'text-brand-purple',
  bgColor: 'bg-brand-purple/10',
  borderColor: 'border-brand-purple/30',
  image: 'https://cdn.magicpatterns.com/uploads/fpkTK3XTLjHYh2K7ez6e8Q/ChatGPT_Image_19_jun_2026,_04_01_09_p.m..png',
  desc: 'Aprendé a descubrir el mensaje energético oculto detrás de los nombres. Una herramienta complementaria para enriquecer tus sesiones y abrir nuevas líneas de interpretación.',
  result: '✅ Nuevas perspectivas para tus consultantes.',
  price: '$12.000'
}, {
  icon: FlaskConical,
  title: 'ALQUIMIA DE VELAS BLANCAS',
  pages: 'Bonus Estratégico',
  color: 'text-brand-goldDark',
  bgColor: 'bg-brand-gold/10',
  borderColor: 'border-brand-gold/30',
  image: 'https://cdn.magicpatterns.com/uploads/kceXs1qePkLbsjC98aeatf/ChatGPT_Image_19_jun_2026,_04_14_27_p.m._(1)_(1).png',
  desc: 'Rituales, limpiezas y prácticas energéticas para acompañar procesos de transformación. Una guía práctica para utilizar la energía de las velas blancas con intención y propósito.',
  result: '✅ Recursos simples para complementar tu trabajo terapéutico.',
  price: '$10.000'
}, {
  icon: EyeOff,
  title: 'PINEAL: EL MÚSCULO DE LA CERTEZA',
  pages: 'Bonus Estratégico',
  color: 'text-brand-goldDark',
  bgColor: 'bg-brand-goldLight/10',
  borderColor: 'border-brand-goldLight/40',
  image: 'https://cdn.magicpatterns.com/uploads/h5qCfDRV1J8xbPivJ6kbGX/ChatGPT_Image_19_jun_2026,_04_14_27_p.m._(2)_(1).png',
  desc: 'Cómo fortalecer tu percepción, desarrollar confianza y reducir la duda durante las lecturas. Porque muchas veces el problema no es la conexión. Es confiar en ella.',
  result: '✅ Más claridad durante las sesiones.',
  price: '$8.000'
}];
const planComparison = [{
  situation: 'Querés atraer más consultantes',
  without: 'Dependés del boca en boca',
  with: 'Tenés un sistema de posicionamiento'
}, {
  situation: 'Te escriben por WhatsApp',
  without: 'No sabés qué responder',
  with: 'Tenés mensajes listos para usar'
}, {
  situation: 'Querés crecer en redes sociales',
  without: 'No sabés qué publicar',
  with: 'Tenés contenido para todo el año'
}, {
  situation: 'Tu agenda tiene espacios vacíos',
  without: 'Esperás que lleguen consultas',
  with: 'Construís una práctica sostenible'
}, {
  situation: 'Sentís que nadie entiende lo que hacés',
  without: 'Tu mensaje es confuso',
  with: 'Comunicás con claridad y confianza'
}, {
  situation: 'Querés vivir de tus terapias',
  without: 'No sabés por dónde empezar',
  with: 'Tenés un plan concreto de crecimiento'
}];
const mockupImage = 'https://cdn.magicpatterns.com/uploads/b6bn2FsbRhAnjKgJx78o8z/ChatGPT_Image_19_jun_2026,_03_21_04_p.m..png';
export const MethodOverview = () => {
  return <Section id="method" className="relative" data-id="element-273">
      <FeatherQuill className="hidden lg:block absolute top-20 right-[3%] w-8 h-8 opacity-25 animate-float-soft" color="#7C3AED" style={{
      transform: 'rotate(-15deg)'
    }} data-id="element-274" />
      <MoonPhase className="hidden lg:block absolute top-60 left-[2%] w-10 h-10 opacity-30 animate-float-soft" color="#A78BFA" style={{
      transform: 'rotate(12deg)',
      animationDelay: '1.5s'
    }} data-id="element-275" />

      <div className="text-center mb-12" data-id="element-276">
        <div className="inline-flex items-center space-x-2 bg-white border border-brand-purple/40 px-4 py-1.5 rounded-full mb-6 shadow-sm" data-id="element-277">
          <span className="text-xl" data-id="element-278">🏆</span>
          <span className="font-serif font-semibold tracking-wider text-brand-purple text-sm md:text-base" data-id="element-279">
            KIT PROFESIONAL DEL INTERVENTOR AKÁSHICO™
          </span>
        </div>
        <h2 className="text-2xl md:text-5xl font-display font-bold mb-4 leading-tight text-brand-purpleDark" data-id="element-280">
          El sistema completo para interpretar, intervenir y acompañar con
          confianza.
        </h2>
        <p className="text-brand-text/70 text-base md:text-lg" data-id="element-281">
          Todo lo que necesitás para trabajar con más claridad, más seguridad y
          mejores resultados.
        </p>
      </div>

      <div className="max-w-5xl mx-auto mb-12 space-y-6" data-id="element-282">
        <div className="rounded-2xl p-6 md:p-8 border bg-brand-purpleDark/15 border-brand-purpleDark/30 backdrop-blur-sm shadow-sm" data-id="element-283">
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6" data-id="element-284">
            <div className="shrink-0 mx-auto md:mx-0 w-28 md:w-36 flex items-center justify-center" data-id="element-285">
              <img src="https://cdn.magicpatterns.com/uploads/hBoFsbYAJcCMm3vGtb1APy/ChatGPT_Image_19_jun_2026,_03_49_32_p.m..png" alt="Tomo I — Tratado del Interventor Akáshico" className="w-full h-auto object-contain rounded-md drop-shadow-xl" data-id="element-286" />
            </div>
            <div className="flex-grow" data-id="element-287">
              <h4 className="font-serif font-bold mb-2 text-lg text-brand-purpleDark" data-id="element-288">
                📘 TRATADO DEL INTERVENTOR AKÁSHICO™
              </h4>
              <p className="text-brand-text/90 font-medium mb-2 text-base" data-id="element-289">
                El manual profesional para terapeutas que quieren llevar sus
                sesiones a otro nivel.
              </p>
              <p className="text-brand-text text-sm mb-4" data-id="element-290">
                Este no es un libro sobre teoría. Es un sistema práctico
                diseñado para ayudarte a interpretar con más claridad,
                intervenir con más confianza y acompañar procesos más profundos.
              </p>

              <div className="bg-white/80 p-4 rounded-lg border border-brand-purple/15 mb-4" data-id="element-291">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-purple mb-3" data-id="element-292">
                  ¿Qué vas a encontrar adentro?
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1.5 text-xs md:text-sm text-brand-text" data-id="element-293">
                  {tratadoFeatures.map((f, i) => <div key={i} className="flex items-center" data-id="element-294">
                      <span data-id="element-295">{f}</span>
                    </div>)}
                </div>
              </div>
              <p className="font-serif italic text-brand-purple text-sm" data-id="element-296">
                🌿 Porque abrir registros es una cosa. Intervenir
                profesionalmente es otra.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mb-8 mt-12" data-id="element-297">
          <h3 className="text-xl md:text-2xl font-serif font-bold text-brand-purpleDark" data-id="element-298">
            🎁 3 BONUS ESTRATÉGICOS
          </h3>
          <p className="text-xs md:text-sm uppercase tracking-wider text-brand-goldDark mt-1 font-bold" data-id="element-299">
            SIEMPRE INCLUIDOS SIN COSTO ADICIONAL
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-300">
          {bonuses.map((bonus, i) => {
          const Icon = bonus.icon;
          const numberLabel = String(i + 1).padStart(2, '0');
          return <div key={i} className="glass-panel rounded-2xl p-5 border-brand-purple/30 relative overflow-hidden bg-white/90 flex flex-col" data-id="element-301">
                <div className={`absolute top-3 left-3 ${bonus.bgColor} border ${bonus.borderColor} ${bonus.color} text-[11px] md:text-sm font-mono font-bold px-2 py-0.5 rounded-md`} data-id="element-302">
                  {numberLabel}
                </div>
                <div className="absolute top-0 right-0 bg-brand-gold text-brand-purpleDark text-xs md:text-sm font-bold px-3 py-1 rounded-bl-lg" data-id="element-303">
                  GRATIS
                </div>

                {bonus.image ? <img src={bonus.image} alt={bonus.title} className="w-14 h-auto mt-6 mb-4 rounded-sm object-contain ring-1 ring-black/10 shadow-md" data-id="element-304" /> : <div className={`relative w-8 h-11 mt-6 mb-4 rounded-sm shadow-sm ${bonus.bgColor.replace('/10', '')} ring-1 ring-black/10 overflow-hidden`} aria-hidden="true" data-id="element-305">
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-black/15" data-id="element-306" />
                    <div className="absolute top-1/2 left-1 right-0.5 h-px bg-white/30" data-id="element-307" />
                  </div>}

                <h4 className="text-base font-serif font-bold mb-2 leading-tight text-brand-text" data-id="element-308">
                  {bonus.title}
                </h4>
                <p className={`${bonus.color} text-xs font-medium mb-3`} data-id="element-309">
                  {bonus.pages}
                </p>
                <p className="text-brand-text mb-3 text-xs flex-grow" data-id="element-310">
                  {bonus.desc}
                </p>
                <div className="text-xs font-medium text-brand-purple mb-2" data-id="element-311">
                  {bonus.result}
                </div>
                <div className="text-xs text-brand-text/50 mt-auto" data-id="element-312">
                  <span data-id="element-313">
                    Valor estimado:{' '}
                    <span className="line-through" data-id="element-314">{bonus.price}</span>
                  </span>
                  <span className="ml-2 font-bold text-brand-goldDark" data-id="element-315">
                    → GRATIS
                  </span>
                </div>
              </div>;
        })}
        </div>

        <div className="text-center mt-8" data-id="element-316">
          <div className="inline-block glass-panel px-6 py-3 rounded-full border-brand-purple/30 bg-white/80" data-id="element-317">
            <p className="text-sm md:text-base font-medium text-brand-text" data-id="element-318">
              🎁 Valor total de los bonus:{' '}
              <span className="line-through text-brand-text/50" data-id="element-319">$30.000</span>{' '}
              <span className="text-brand-purple font-bold" data-id="element-320">
                → Hoy incluidos GRATIS con tu compra.
              </span>
            </p>
          </div>
        </div>
      </div>

      <LaunchUrgency data-id="element-321" />
      <Pricing data-id="element-322" />

      <div className="max-w-4xl mx-auto mb-12 mt-12" data-id="element-323">
        <div className="bg-white rounded-2xl p-5 md:p-6 border border-brand-purple/30 shadow-sm" data-id="element-324">
          <p className="text-base md:text-lg font-semibold text-brand-purpleDark mb-4 text-center" data-id="element-325">
            🎓 ¿QUÉ AGREGA EL KIT EL ALTAR DEL TERAPEUTA AKÁSHICO?
          </p>

          <div className="hidden md:block" data-id="element-326">
            <div className="grid grid-cols-3 gap-2 text-[11px] md:text-sm font-semibold uppercase tracking-wide text-brand-text/60 border-b border-brand-purple/20 pb-2 mb-2" data-id="element-327">
              <div data-id="element-328">Situación</div>
              <div className="text-brand-burgundy" data-id="element-329">❌ Sin Mega Kit</div>
              <div className="text-brand-purple" data-id="element-330">✅ Con Mega Kit</div>
            </div>
            <div className="space-y-2" data-id="element-331">
              {planComparison.map((row, i) => <div key={i} className="grid grid-cols-3 gap-2 text-xs md:text-sm text-brand-text py-1.5 border-b border-brand-purple/10 last:border-0" data-id="element-332">
                  <div className="font-medium" data-id="element-333">{row.situation}</div>
                  <div className="text-brand-burgundy/80" data-id="element-334">{row.without}</div>
                  <div className="text-brand-purple font-medium" data-id="element-335">
                    {row.with}
                  </div>
                </div>)}
            </div>
          </div>

          <div className="md:hidden space-y-3" data-id="element-336">
            {planComparison.map((row, i) => <div key={i} className="border-b border-brand-purple/15 pb-3 last:border-0 last:pb-0" data-id="element-337">
                <p className="text-xs font-semibold text-brand-text mb-1.5" data-id="element-338">
                  {row.situation}
                </p>
                <div className="text-[11px] text-brand-burgundy/80 mb-0.5" data-id="element-339">
                  ❌ {row.without}
                </div>
                <div className="text-[11px] text-brand-purple font-medium" data-id="element-340">
                  ✅ {row.with}
                </div>
              </div>)}
          </div>

          <p className="text-sm font-medium text-brand-goldDark italic text-center mt-5" data-id="element-341">
            🌿 Un solo consultante nuevo conseguido gracias a este sistema puede
            pagar la diferencia de precio por completo.
          </p>
        </div>
      </div>
    </Section>;
};